import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, message } from 'antd';
import { CloseCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import {
	InputWrapper,
	AddressInput,
	ClearIconButton,
	MapIconButton,
	MapModalContainer,
	SuggestionsDropdown,
	SuggestionItem,
} from './AddressPickerV2.styled';

/**
 * AddressPickerV2 Component
 *
 * A reusable address picker component with Google Places Autocomplete
 * and interactive map selection. Automatically fetches latitude/longitude
 * from addresses and supports reverse geocoding.
 *
 * Uses AutocompleteService (instead of Autocomplete widget) to support:
 * - Up to 25 suggestions
 * - Establishment/POI name search (e.g. "Bangalore Intelligent Retail")
 * - Full geocode + establishment type queries
 */
function AddressPickerV2({
	value = '',
	latitude,
	longitude,
	onChange,
	disabled = false,
	placeholder = 'Type address or use map to select location...',
}) {
	const [addressInput, setAddressInput] = useState(value || '');
	const [mapVisible, setMapVisible] = useState(false);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
	const [suggestions, setSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);

	const inputRef = useRef(null);
	const wrapperRef = useRef(null);
	const autocompleteServiceRef = useRef(null);
	const placesServiceRef = useRef(null);
	const placesServiceDivRef = useRef(null);
	const mapRef = useRef(null);
	const markerRef = useRef(null);
	const geocoderRef = useRef(null);
	const mapContainerRef = useRef(null);
	const reverseGeocodeRef = useRef(null);
	const debounceTimerRef = useRef(null);

	// Default fallback location (Gurgaon, India)
	const defaultLatLng = useMemo(() => ({ lat: 28.4595, lng: 77.0266 }), []);

	// Load Google Maps on mount
	useEffect(() => {
		loadGoogleMapsV2()
			.then(() => {
				setIsGoogleMapsLoaded(true);
			})
			.catch((err) => {
				console.error('Failed to load Google Maps:', err);
				if (err.message && err.message.includes('REACT_APP_GOOGLE_MAPS_KEY')) {
					message.error(
						'Google Maps API key is missing. Please configure REACT_APP_GOOGLE_MAPS_KEY in your environment variables.'
					);
				} else {
					message.warning(
						'Google Maps is taking longer to load. Address autocomplete may not work until it loads.'
					);
				}
			});
	}, []);

	// Initialize AutocompleteService and PlacesService when Google Maps is loaded
	useEffect(() => {
		// Cleanup function
		function cleanup() {
			clearTimeout(debounceTimerRef.current);
			autocompleteServiceRef.current = null;
			placesServiceRef.current = null;
		}

		if (!isGoogleMapsLoaded || autocompleteServiceRef.current) {
			return cleanup;
		}

		try {
			autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();

			geocoderRef.current = new window.google.maps.Geocoder();

			// PlacesService needs a DOM element or a Map instance
			if (!placesServiceDivRef.current) {
				placesServiceDivRef.current = document.createElement('div');
			}
			placesServiceRef.current = new window.google.maps.places.PlacesService(
				placesServiceDivRef.current
			);
		} catch (error) {
			console.error('Error initializing Places services:', error);
			return cleanup;
		}

		return cleanup;
	}, [isGoogleMapsLoaded]);

	// Close suggestions on outside click
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				setShowSuggestions(false);
				setActiveIndex(-1);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Update address input when value prop changes
	useEffect(() => {
		if (value !== addressInput) {
			setAddressInput(value || '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	/**
	 * Extract state, pincode, and city from geocoding result
	 */
	const extractAddressComponents = useCallback((addressComponents) => {
		let state = '';
		let pincode = '';
		let city = '';

		addressComponents.forEach((component) => {
			if (component.types.includes('administrative_area_level_1')) {
				state = component.long_name;
			}
			if (component.types.includes('postal_code')) {
				pincode = component.long_name;
			}
			if (
				component.types.includes('locality') ||
				component.types.includes('administrative_area_level_2')
			) {
				city = component.long_name;
			}
		});

		return { state, pincode, city };
	}, []);

	/**
	 * Handle geocoding error - update with available data (used for map click/drag)
	 */
	const handleGeocodingError = useCallback(
		(lat, lng, formattedAddress) => {
			message.warning('Could not retrieve address details for selected location');
			if (onChange) {
				const addr = formattedAddress || addressInput || '';
				onChange({
					address: addr,
					latitude: String(lat),
					longitude: String(lng),
					state: '',
					pincode: '',
				});
			}
		},
		[addressInput, onChange]
	);

	/**
	 * Handle successful geocoding result (used for map click/drag)
	 * Uses formatted_address from the geocoder response directly.
	 */
	const handleGeocodingSuccess = useCallback(
		(result, lat, lng) => {
			const { state, pincode } = extractAddressComponents(result.address_components);
			const addr = result.formatted_address || addressInput;

			setAddressInput(addr);

			if (onChange) {
				onChange({
					address: addr,
					latitude: String(lat),
					longitude: String(lng),
					state: state || '',
					pincode: pincode || '',
				});
			}
		},
		[addressInput, onChange, extractAddressComponents]
	);

	/**
	 * Reverse geocode coordinates (used for map click/drag only).
	 * Uses formatted_address from the geocoder response directly.
	 */
	const reverseGeocode = useCallback(
		(lat, lng) => {
			if (!geocoderRef.current) return;

			geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
				if (status !== 'OK' || !results || results.length === 0) {
					handleGeocodingError(lat, lng, null);
					return;
				}
				handleGeocodingSuccess(results[0], lat, lng);
			});
		},
		[handleGeocodingError, handleGeocodingSuccess]
	);

	// Store reverseGeocode in ref so map listeners can access latest version
	useEffect(() => {
		reverseGeocodeRef.current = reverseGeocode;
	}, [reverseGeocode]);

	/**
	 * Select a suggestion: resolve lat/lng + address directly from PlacesService.getDetails.
	 * Uses place.formatted_address — no reverse geocode needed.
	 */
	const selectSuggestion = useCallback(
		(prediction) => {
			if (!placesServiceRef.current) return;

			setAddressInput(prediction.description);
			setSuggestions([]);
			setShowSuggestions(false);
			setActiveIndex(-1);

			placesServiceRef.current.getDetails(
				{
					placeId: prediction.place_id,
					fields: ['geometry', 'formatted_address', 'address_components'],
				},
				(place, status) => {
					if (
						status !== window.google.maps.places.PlacesServiceStatus.OK ||
						!place ||
						!place.geometry
					) {
						message.warning('No details available for the selected address');
						return;
					}

					const lat = place.geometry.location.lat();
					const lng = place.geometry.location.lng();
					const placeName = prediction.structured_formatting?.main_text || '';
					const formattedAddr = place.formatted_address || prediction.description;
					// Prepend the establishment/place name if it's not already part of the address
					const addr =
						placeName && !formattedAddr.startsWith(placeName)
							? `${placeName}, ${formattedAddr}`
							: formattedAddr;
					const { state, pincode } = extractAddressComponents(
						place.address_components || []
					);

					setAddressInput(addr);

					if (onChange) {
						onChange({
							address: addr,
							latitude: String(lat),
							longitude: String(lng),
							state: state || '',
							pincode: pincode || '',
						});
					}
				}
			);
		},
		[onChange, extractAddressComponents]
	);

	/**
	 * Fetch suggestions via two parallel calls (establishment + geocode).
	 * Each call returns up to 5; merged & deduplicated gives up to 10 results.
	 * Google Places API hard-limits each getPlacePredictions call to 5 results
	 * and only accepts a single type per call.
	 */
	const fetchSuggestions = useCallback((query) => {
		if (!autocompleteServiceRef.current || !query || query.length < 2) {
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}

		const { OK } = window.google.maps.places.PlacesServiceStatus;
		const common = { input: query, componentRestrictions: { country: 'in' } };
		const combined = [];
		let pending = 2;

		const finish = () => {
			pending -= 1;
			if (pending > 0) return;

			// Deduplicate by place_id, preserving establishment-first order
			const seen = new Set();
			const unique = combined.filter((p) => {
				if (seen.has(p.place_id)) return false;
				seen.add(p.place_id);
				return true;
			});

			setSuggestions(unique.slice(0, 25));
			setShowSuggestions(unique.length > 0);
			setActiveIndex(-1);
		};

		// Call 1: named places / businesses
		autocompleteServiceRef.current.getPlacePredictions(
			{ ...common, types: ['establishment'] },
			(predictions, status) => {
				if (status === OK && predictions) combined.push(...predictions);
				finish();
			}
		);

		// Call 2: addresses / geocode results
		autocompleteServiceRef.current.getPlacePredictions(
			{ ...common, types: ['geocode'] },
			(predictions, status) => {
				if (status === OK && predictions) combined.push(...predictions);
				finish();
			}
		);
	}, []);

	/**
	 * Handle manual address input change — debounce suggestions fetch
	 */
	const handleInputChange = (e) => {
		const newValue = e.target.value;
		setAddressInput(newValue);
		setActiveIndex(-1);

		if (!newValue && onChange) {
			onChange({ address: '', latitude: '', longitude: '', state: '', pincode: '' });
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}

		// Debounce the autocomplete call by 300ms
		clearTimeout(debounceTimerRef.current);
		debounceTimerRef.current = setTimeout(() => {
			fetchSuggestions(newValue);
		}, 300);
	};

	const handleClear = useCallback(() => {
		clearTimeout(debounceTimerRef.current);
		setAddressInput('');
		setSuggestions([]);
		setShowSuggestions(false);
		setActiveIndex(-1);
		if (onChange) {
			onChange({ address: '', latitude: '', longitude: '', state: '', pincode: '' });
		}
		inputRef.current?.focus();
	}, [onChange]);

	/**
	 * Keyboard navigation for the suggestions list
	 */
	const handleKeyDown = (e) => {
		if (!showSuggestions || suggestions.length === 0) return;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveIndex((prev) => Math.max(prev - 1, -1));
		} else if (e.key === 'Enter' && activeIndex >= 0) {
			e.preventDefault();
			selectSuggestion(suggestions[activeIndex]);
		} else if (e.key === 'Escape') {
			setShowSuggestions(false);
			setActiveIndex(-1);
		}
	};

	/**
	 * Get current location (geolocation API)
	 */
	const getCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) {
			message.warning('Geolocation is not supported by your browser');
			return defaultLatLng;
		}

		return new Promise((resolve) => {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
				},
				() => {
					message.warning('Could not get current location. Using default location.');
					resolve(defaultLatLng);
				},
				{ timeout: 10000 }
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Get initial map center location
	 */
	const getInitialCenter = useCallback(async () => {
		if (latitude && longitude) {
			const lat = parseFloat(latitude);
			const lng = parseFloat(longitude);
			if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
				return { lat, lng };
			}
		}
		return getCurrentLocation();
	}, [latitude, longitude, getCurrentLocation]);

	/**
	 * Initialize map in modal
	 */
	const initializeMap = useCallback(async () => {
		if (!isGoogleMapsLoaded || !mapContainerRef.current) return;

		if (mapRef.current && mapContainerRef.current) {
			try {
				const initialCenter = await getInitialCenter();
				mapRef.current.setCenter(initialCenter);
				if (markerRef.current) {
					markerRef.current.setPosition(initialCenter);
				}
				window.google.maps.event.trigger(mapRef.current, 'resize');
				return;
			} catch (error) {
				console.error('Error updating existing map:', error);
				mapRef.current = null;
				markerRef.current = null;
			}
		}

		try {
			const initialCenter = await getInitialCenter();

			mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
				center: initialCenter,
				zoom: 15,
				mapTypeControl: true,
				streetViewControl: false,
			});

			setTimeout(() => {
				if (mapRef.current) {
					window.google.maps.event.trigger(mapRef.current, 'resize');
				}
			}, 100);

			markerRef.current = new window.google.maps.Marker({
				position: initialCenter,
				map: mapRef.current,
				draggable: true,
			});

			mapRef.current.addListener('click', (e) => {
				const lat = e.latLng.lat();
				const lng = e.latLng.lng();
				markerRef.current.setPosition({ lat, lng });
				if (reverseGeocodeRef.current) {
					reverseGeocodeRef.current(lat, lng);
				}
				setMapVisible(false);
			});

			markerRef.current.addListener('dragend', (e) => {
				const lat = e.latLng.lat();
				const lng = e.latLng.lng();
				if (reverseGeocodeRef.current) {
					reverseGeocodeRef.current(lat, lng);
				}
			});
		} catch (error) {
			console.error('Error initializing map:', error);
			message.error('Failed to initialize map');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isGoogleMapsLoaded, latitude, longitude, getCurrentLocation, reverseGeocode]);

	/**
	 * Open map modal
	 */
	const handleOpenMap = useCallback(() => {
		if (!isGoogleMapsLoaded) {
			message.warning('Google Maps is still loading. Please wait...');
			return;
		}
		setMapVisible(true);
	}, [isGoogleMapsLoaded]);

	/**
	 * Close map modal
	 */
	const handleCloseMap = useCallback(() => {
		setMapVisible(false);
	}, []);

	/**
	 * Handle map modal open - initialize map when modal is visible
	 */
	useEffect(() => {
		if (!mapVisible) {
			const cleanupTimer = setTimeout(() => {
				if (markerRef.current) {
					window.google?.maps?.event?.clearInstanceListeners?.(markerRef.current);
					markerRef.current = null;
				}
				if (mapRef.current) {
					window.google?.maps?.event?.clearInstanceListeners?.(mapRef.current);
					mapRef.current = null;
				}
			}, 200);
			return () => clearTimeout(cleanupTimer);
		}

		if (!isGoogleMapsLoaded) return undefined;

		const timer = setTimeout(() => {
			if (mapContainerRef.current) {
				initializeMap();
			}
		}, 300);

		const resizeTimer = setTimeout(() => {
			if (mapRef.current) {
				window.google.maps.event.trigger(mapRef.current, 'resize');
			}
		}, 600);

		return () => {
			clearTimeout(timer);
			clearTimeout(resizeTimer);
		};
	}, [mapVisible, isGoogleMapsLoaded, initializeMap]);

	return (
		<>
			<InputWrapper ref={wrapperRef}>
				<AddressInput
					ref={inputRef}
					value={addressInput}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={(e) => {
						if (addressInput) {
							e.target.select();
						}
						if (suggestions.length > 0) setShowSuggestions(true);
					}}
					placeholder={placeholder}
					disabled={disabled || !isGoogleMapsLoaded}
					autoComplete="off"
				/>
				{addressInput ? (
					<ClearIconButton
						type="button"
						onClick={handleClear}
						disabled={disabled}
						title="Clear selected address"
					>
						<CloseCircleOutlined />
					</ClearIconButton>
				) : (
					<MapIconButton
						type="button"
						onClick={handleOpenMap}
						disabled={disabled || !isGoogleMapsLoaded}
						title="Select location on map"
					>
						<EnvironmentOutlined />
					</MapIconButton>
				)}

				{showSuggestions && suggestions.length > 0 && (
					<SuggestionsDropdown>
						{suggestions.map((prediction, index) => {
							const mainText =
								prediction.structured_formatting?.main_text ||
								prediction.description;
							const secondaryText =
								prediction.structured_formatting?.secondary_text || '';
							return (
								<SuggestionItem
									key={prediction.place_id}
									className={index === activeIndex ? 'active' : ''}
									onMouseDown={(e) => {
										// mousedown fires before input blur, prevents dropdown from closing
										e.preventDefault();
										selectSuggestion(prediction);
									}}
								>
									<span className="suggestion-main">{mainText}</span>
									{secondaryText && (
										<span className="suggestion-secondary">
											{secondaryText}
										</span>
									)}
								</SuggestionItem>
							);
						})}
					</SuggestionsDropdown>
				)}
			</InputWrapper>

			<Modal
				title="Select Location on Map"
				open={mapVisible}
				onCancel={handleCloseMap}
				footer={null}
				width={1000}
				destroyOnClose
				styles={{
					body: {
						padding: 0,
						height: '600px',
						minHeight: '600px',
					},
				}}
			>
				<MapModalContainer>
					<div className="map-container">
						<div ref={mapContainerRef} id="map" className="map-wrapper" />
					</div>
				</MapModalContainer>
			</Modal>
		</>
	);
}

AddressPickerV2.propTypes = {
	value: PropTypes.string,
	latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
};

AddressPickerV2.defaultProps = {
	value: '',
	latitude: '',
	longitude: '',
	disabled: false,
	placeholder: 'Type address or use map to select location...',
};

export default AddressPickerV2;
